import { ObservableDataSource } from '@principle-theorem/ng-shared';
import {
  IBaseChartBuilderConfig,
  type ICustomReportFilterValue,
} from '@principle-theorem/principle-core/interfaces';
import {
  CrossfilterGroup,
  ICanGroupMeasuresProperty,
  IChartConfig,
  IMeasureBuilderData,
} from '@principle-theorem/reporting/interfaces';
import {
  isDate,
  isObject,
  toISODate,
  TypeGuard,
  WithId,
} from '@principle-theorem/shared';
import crossfilter, {
  ComparableValue,
  Dimension,
  NaturallyOrderedValue,
} from 'crossfilter2';
import type * as dc from 'dc';
import { isNull, isString } from 'lodash';
import { type Subject } from 'rxjs';
import {
  DCDataTransformer,
  type IDCTransformResult,
} from '../../../../models/report/charts/data-transformers/dc-data-transformer';
import { ITableRow } from '../table-chart-builder';
import { type IDCTooltip } from './dc-tooltip';

export const D3_CHART_ELEM_SELECTOR = 'd3-chart';

export interface IDCChartFilter {
  label: string;
  valueOf(): string | number;
}

export interface IDCChartEvent {
  config: IChartConfig;
  groupByDimension: ICanGroupMeasuresProperty;
  filterValues: ICustomReportFilterValue[];
  isReset: boolean;
}

export enum ChartBuilderType {
  Table = 'table',
  DC = 'dc',
}

export interface ITableBuilder {
  type: ChartBuilderType.Table;
  getLabel(config: IChartConfig): string;
  getChart(
    results: IDCTransformResult[],
    config: IChartConfig,
    eventListener$?: Subject<IDCChartEvent>
  ): ObservableDataSource<ITableRow> | undefined;
  getColumns(
    results: IDCTransformResult[],
    config: IChartConfig
  ): WithId<{ id: string; label: string }>[];
}

export interface IDCChartBuilder<T> {
  type: ChartBuilderType.DC;
  getLabel(config: IChartConfig): string;
  getChart(
    results: IDCTransformResult[],
    config: IChartConfig,
    eventListener$?: Subject<IDCChartEvent>
  ): dc.BaseMixin<T> | undefined;
  getLegend?(): dc.HtmlLegend | dc.Legend;
  getTooltip?(config: IChartConfig): IDCTooltip;
  getChartClasses?(config: IChartConfig): string[];
}

export interface ICanBuildForCompositeChart<T> {
  build(
    parent: string | dc.CompositeChart,
    result: IDCTransformResult,
    config: IChartConfig,
    measure: IMeasureBuilderData
  ): dc.BaseMixin<T>;
}

export const DEFAULT_CHART_BUILDER_CONFIG: IBaseChartBuilderConfig = {
  height: 200,
  width: 200,
};

export function applyBaseConfigToChart<T>(
  config: IBaseChartBuilderConfig,
  chart: dc.BaseMixin<T>
): void {
  chart.height(config.height);
  chart.width(config.width);
  chart.title(() => '');
  if (!config.disableControls) {
    chart.turnOnControls();
  }
}

type DCFilter = string | [Date, Date] | IDCChartFilter;

interface IGroupFilters {
  factFilter: (fact: unknown) => boolean;
  groupFilter: (groupValue: ComparableValue) => boolean;
}

export class ChartBuildHelpers {
  static resolveChartFilters(
    chart: dc.BaseMixin<unknown>
  ): ICustomReportFilterValue[] {
    const filters = chart.filters() as DCFilter[];
    return filters.map((filter) => ChartBuildHelpers.getFilterValue(filter));
  }

  static onFilteredEvent(
    chart: dc.BaseMixin<unknown>,
    filterValue: unknown,
    config: IChartConfig,
    eventListener$: Subject<IDCChartEvent>
  ): void {
    const filterValues = this.resolveChartFilters(chart);
    const event = this.getDCChartEvent(config, filterValues, filterValue);
    eventListener$.next(event);
  }

  static getDCChartEvent(
    config: IChartConfig,
    filterValues: ICustomReportFilterValue[],
    eventValue: unknown
  ): IDCChartEvent | undefined {
    const groupByDimension = config.builderData.groupByDimension;
    if (!groupByDimension) {
      // eslint-disable-next-line no-console
      console.error('No groupByDimension found for chart', config);
      return;
    }
    return {
      config,
      filterValues,
      groupByDimension,
      isReset: isNull(eventValue),
    };
  }

  static buildFilteredDimension(
    result: IDCTransformResult,
    config: IChartConfig,
    measure?: IMeasureBuilderData
  ): {
    dimension: Dimension<unknown, NaturallyOrderedValue>;
    group?: CrossfilterGroup;
  } {
    const transformer = new DCDataTransformer(result.labelTransformFn);

    const dimension = transformer.getDimension(
      result.factTable,
      config,
      result.measure
    );
    const group = transformer.getGroup(result.measure, dimension);
    if (!group || !measure || !measure.filters?.length) {
      return { dimension, group };
    }

    const filters = this.buildGroupFilters(result, config, measure);
    return {
      dimension,
      group: transformer.getFilteredGroup(
        group,
        filters.factFilter,
        filters.groupFilter
      ),
    };
  }

  static buildGroupFilters(
    result: IDCTransformResult,
    config: IChartConfig,
    measure: IMeasureBuilderData
  ): IGroupFilters {
    const filters = measure.filters ?? [];

    const factFilter = (fact: unknown): boolean => {
      const dataResults = [result.measure.measure.dataAccessor(fact)].flat();
      return dataResults.some((dataResult) =>
        this.matchesFilters(dataResult, filters)
      );
    };

    const groupById = config.builderData.groupByDimension?.metadata.id;
    const measureId = measure.measure.metadata.id;
    const isGroupedByFilteredMeasure = groupById === measureId;

    const groupFilter = (groupValue: ComparableValue): boolean =>
      isGroupedByFilteredMeasure
        ? this.matchesFilters(groupValue, filters)
        : true;

    return { factFilter, groupFilter };
  }

  static matchesFilters(
    dataResult: crossfilter.ComparableValue,
    filters: crossfilter.ComparableValue[]
  ): boolean {
    if (!filters.length) {
      return true;
    }
    return filters.some((filter) => {
      if (isObject(dataResult)) {
        return dataResult.valueOf() === filter;
      }
      return filter === dataResult;
    });
  }

  static getFilterValue(filter: DCFilter): ICustomReportFilterValue {
    if (isString(filter)) {
      return {
        label: filter,
        value: filter,
      };
    }
    if (ChartBuildHelpers.isDateRangeFilter(filter)) {
      const from = toISODate(filter[0]);
      const to = toISODate(filter[1]);
      return {
        label: `${from} - ${to}`,
        value: [from, to],
      };
    }
    return {
      label: filter.label,
      value: filter.valueOf() ?? undefined,
    };
  }

  static isDateRangeFilter(data: unknown): data is [Date, Date] {
    return TypeGuard.arrayOf(isDate)(data) && data.length === 2;
  }
}
